import { z } from "zod";

import { voiceProviderIdentitySchema } from "@/shared/models/provider-name";

export const providerSchema = voiceProviderIdentitySchema
  .pick({ displayName: true })
  .extend({ id: z.string().min(1) })
  .strict();
export type SchedulingProvider = z.infer<typeof providerSchema>;

export const schedulingResourceSchema = z.strictObject({ id: z.string().min(1), displayName: z.string() });
export type SchedulingResource = z.infer<typeof schedulingResourceSchema>;

/** Facts about one service block, shared by booked appointments and offered slots. */
export const schedulingServiceSchema = z.strictObject({
  // Customer-approved data, not speech or a scheduling-policy instruction.
  serviceLabel: z.string().trim().min(1).optional(),
  // The actual clinical practitioner, when known; never infer one from a resource name.
  provider: providerSchema.optional(),
  // Schedule/resource attribution, not evidence that another resource has been reserved.
  resource: schedulingResourceSchema.optional(),
});
export type SchedulingService = z.infer<typeof schedulingServiceSchema>;

/** Model-facing facts. IDs remain in runtime state, never in presentation context. */
export const schedulingServiceDisplaySchema = z.object({
  serviceLabel: schedulingServiceSchema.shape.serviceLabel,
  provider: providerSchema.pick({ displayName: true }).strip().optional(),
  resource: schedulingResourceSchema.pick({ displayName: true }).strip().optional(),
});

/** Select only explicitly supplied facts without copying operational fields or signed references. */
export function projectSchedulingService(service: SchedulingService): SchedulingService {
  return {
    ...(service.serviceLabel !== undefined && { serviceLabel: service.serviceLabel }),
    ...(service.provider !== undefined && { provider: service.provider }),
    ...(service.resource !== undefined && { resource: service.resource }),
  };
}

/** Sanitize just service metadata at a channel's existing display boundary, retaining its other fields. */
export function withSchedulingServiceDisplay<T extends SchedulingService>(value: T) {
  const { serviceLabel, provider, resource, ...rest } = value;

  return {
    ...rest,
    ...schedulingServiceDisplaySchema.parse(projectSchedulingService({ serviceLabel, provider, resource })),
  };
}

export enum SchedulingPresentation {
  SingleVisit = "single_visit",
}

// Describes an already-authorized unit/plan; it cannot group appointments or authorize a write.
export const schedulingPresentationSchema = z.enum(SchedulingPresentation);
